import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, X } from 'lucide-react';
import { overlayDismiss } from '../../lib/overlayDismiss';

const MAX_LIST = 20;

/**
 * 禁撥名單的刪除確認 modal（單筆／批次／清空三處共用）。
 *
 * 樣式沿用後台既有慣例 `IvrAgents/DeleteAvatarDialog.jsx`，不自創：
 * overlayDismiss + `bg-ink-900/30 backdrop-blur-sm` 遮罩 + `bg-cream-50 … max-w-md` 面板
 * + 頭尾 `border-cream-200` 分隔 + `AlertTriangle`/`text-danger-500` 標題 + 右上 X
 * + footer 右對齊 ghost 取消 / `bg-danger-500` 主鍵。
 *
 * 🔴 **一定要列出號碼**，不可只說「確定刪除 N 筆」——
 *    刪錯的後果是「那個人又開始被打電話」，而且**零訊號**（沒有失敗、沒有客訴、沒有異常紀錄）。
 *    超過 20 筆才截斷並顯示「… +M」。
 *
 * `confirmWord` 有值時＝毀滅性操作（清空整份名單）：要輸入指定文字才解鎖，
 * 取代原本「兩個 window.confirm 疊起來」的做法。
 *
 * @param {Array<{phone:string, note?:string}>} items 要刪除的項目
 */
export function ConfirmDeleteDialog({
    open, items = [], onClose, onConfirm, busy = false, error = null, confirmWord = null,
}) {
    const { t } = useTranslation('outbound');
    const [typed, setTyped] = useState('');
    const inputRef = useRef(null);

    useEffect(() => {
        if (open) {
            setTyped('');
            if (confirmWord) setTimeout(() => inputRef.current?.focus(), 50);
        }
    }, [open, confirmWord]);

    if (!open) return null;

    const n = items.length;
    const shown = items.slice(0, MAX_LIST);
    const rest = n - shown.length;
    const locked = !!confirmWord && typed.trim() !== confirmWord;
    const canDelete = !locked && !busy && n > 0;

    return (
        <div className="fixed inset-0 bg-ink-900/30 backdrop-blur-sm z-50 grid place-items-center p-4" {...overlayDismiss(onClose)}>
            <div className="bg-cream-50 border border-cream-200 rounded-lg shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-6 py-4 border-b border-cream-200">
                    <div className="flex items-center gap-2">
                        <AlertTriangle size={18} strokeWidth={1.8} className="text-danger-500" />
                        <h2 className="heading-display text-lg font-semibold text-ink-900">
                            {confirmWord ? t('bl.confirm.clearTitle') : t('bl.confirm.title')}
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={busy}
                        className="size-8 inline-flex items-center justify-center rounded-md text-ink-400 hover:text-ink-700 hover:bg-cream-100 transition disabled:opacity-50"
                    >
                        <X size={16} />
                    </button>
                </div>

                <div className="px-6 py-5 space-y-4">
                    <div className="text-sm text-ink-700 leading-relaxed">
                        {t('bl.confirm.lead1')}<span className="font-semibold text-danger-500">{n}</span>{t('bl.confirm.lead2')}
                        <span className="font-semibold text-ink-900">{t('bl.confirm.willBeDialedAgain')}</span>。
                    </div>

                    {/* 🔴 號碼清單不可省 */}
                    <ul className="rounded-md bg-white border border-cream-200 px-3.5 py-3 space-y-1.5 max-h-56 overflow-auto">
                        {shown.map((it) => (
                            <li key={it.phone} className="flex items-baseline gap-3.5 text-[12.5px]">
                                <span className="font-mono tabular-nums text-ink-900 shrink-0 w-[108px]">{it.phone}</span>
                                <span className="text-ink-400 text-[11.5px] truncate">{it.note || '—'}</span>
                            </li>
                        ))}
                        {rest > 0 && <li className="text-ink-400 text-[11.5px]">… +{rest}</li>}
                    </ul>

                    {confirmWord && (
                        <div>
                            <label className="block font-mono text-[10px] uppercase tracking-wider text-ink-500 mb-1.5">
                                {t('bl.confirm.typeToUnlock', { word: confirmWord })}
                            </label>
                            <input
                                ref={inputRef}
                                type="text"
                                value={typed}
                                onChange={(e) => setTyped(e.target.value)}
                                disabled={busy}
                                placeholder={confirmWord}
                                autoComplete="off"
                                className="w-full px-3 py-2 text-sm border border-cream-300 focus:border-danger-500 outline-none rounded-md bg-white font-mono disabled:bg-cream-100"
                            />
                            {typed && locked && (
                                <div className="text-[11px] text-ink-400 mt-1">{t('bl.confirm.mismatch')}</div>
                            )}
                        </div>
                    )}

                    {error && (
                        <div className="text-sm text-danger-500 bg-danger-100/40 border border-danger-500/30 rounded-md px-3 py-2">
                            {error}
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-cream-200">
                    <button
                        onClick={onClose}
                        disabled={busy}
                        className="px-4 py-2 text-sm rounded-md text-ink-700 hover:bg-cream-100 transition disabled:opacity-50"
                    >
                        {t('bl.cancel')}
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={!canDelete}
                        className="px-4 py-2 text-sm rounded-md bg-danger-500 text-white hover:bg-danger-500/90 transition disabled:bg-cream-300 disabled:text-ink-400 disabled:cursor-not-allowed"
                    >
                        {busy ? t('bl.confirm.deleting') : t('bl.confirm.deleteN', { n })}
                    </button>
                </div>
            </div>
        </div>
    );
}
